import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocations } from '../../hooks/useLocations';
import { fetchLocations } from '../../features/locations/locationsSlice';
import { ACTIONS } from '../../constants/actionTables';
import TableComponent from '../../components/Table';
import ModalAddLocation from '../../components/ModalAddLocation';
import PageLoader from '../../components/PageLoader';
import { requestSearch } from '../../helpers/search';

import { Typography } from '@mui/material';

import styles from './style.module.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

function Locations() {
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const dispatch = useDispatch();

  const { user } = useAuth();

  const { locations, isLoading } = useLocations();
  const { t } = useTranslation();

  // Load locations data when component mounts
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  useEffect(() => {
    if (locations) {
      const preparedData = locations.map((location) => ({
        id: location.id,
        number: location.id,
        name: location.name,
        description: location.description,
        flagForDelete: location.is_able_to_remove,
      }));
      setData(preparedData);
      setFilteredData(preparedData);
    }
  }, [locations]);

  const titles = [t('generic.number'), t('generic.name'), t('generic.description')];

  if (user.role === 'admin') {
    titles.push(t('generic.action'));
  }

  const handleSearch = (searchValue) => {
    // Reset sorting when searching
    if (searchValue.trim() !== '') {
      setSortConfig({ key: null, direction: 'asc' });
    }
    requestSearch(searchValue, data || [], setFilteredData);
  };

  const getLocationsCount = () => {
    return filteredData?.length || 0;
  };

  const handleSort = (columnKey) => {
    // Implement three-state sorting: asc → desc → reset (no sorting)
    let newSortConfig = { key: null, direction: 'asc' };

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        // Switch from asc to desc
        newSortConfig = { key: columnKey, direction: 'desc' };
      } else if (sortConfig.direction === 'desc') {
        // Reset sorting (third click)
        newSortConfig = { key: null, direction: 'asc' };
      }
    } else {
      // New column clicked - start with asc
      newSortConfig = { key: columnKey, direction: 'asc' };
    }

    setSortConfig(newSortConfig);

    // If resetting, restore original order
    if (newSortConfig.key === null) {
      setFilteredData([...data]);
      return;
    }

    // Apply sorting
    const dataToSort = [...filteredData];
    dataToSort.sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return newSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return newSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredData(dataToSort);
  };

  return (
    <PageLoader
      loading={isLoading && !locations}
      headerTitle={t('generic.locations')}
      headerSubtitle={t('generic.locationsSubtitle')}
    >
      <section className={styles.container}>
        <div className={styles.header}>
          <div className={styles.caption}>
            <Typography variant="h5" fontWeight="bold" className={styles.headerTitle}>
              {t('generic.locations')}
            </Typography>
            <Typography className={styles.headerSubtitle}>
              {t('generic.locationsSubtitle')}
            </Typography>
          </div>
          {user.role === 'admin' && <ModalAddLocation type={'add'} />}
        </div>

      {filteredData && (
        <TableComponent
          titles={titles}
          data={filteredData}
          action={ACTIONS.LOCATIONS}
          search={true}
          showTableHeader={true}
          tableTitle={t('generic.locations')}
          tableCount={`${getLocationsCount()} ${t('generic.locations')}`}
          searchPlaceholder={t('generic.search')}
          onSearch={handleSearch}
          onSort={handleSort}
          sortConfig={sortConfig}
          sortableColumns={['number', 'name', 'description']}
          columnKeys={['number', 'name', 'description']}
        />
      )}
    </section>
    </PageLoader>
  );
}

export default Locations;
